import { setItem } from "@app/helpers";
import {
  MAX_WIDTH_MOBILE_SCREEN,
  StorageKey,
  DEFAULT_MAX_YEAR,
} from "@app/constants";
import { parse, format, sub } from "date-fns";
import { setIsLoading, store } from "@app/store";
import { toZonedTime } from "date-fns-tz";

export const isURL = (str: string) => {
  return /http|www/.test(str);
};

export const capitalize = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const delay = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const getInitials = (name: string, maxChar: number) => {
  return name
    .split(/\s/)
    .map((word) => word[0])
    .join("")
    .substr(0, maxChar)
    .toUpperCase();
};

export const scrollToTop = () => {
  try {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  } catch (error) {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }
};

export const scrollTo = (el: Element, callback = () => {}) => {
  const offset = el.scrollHeight;
  const onScroll = () => {
    if (offset - el.clientHeight - el.scrollTop <= 1) {
      el.removeEventListener("scroll", onScroll);
      callback();
    }
  };
  el.addEventListener("scroll", onScroll);
  onScroll();
  try {
    el.scrollTo({
      top: offset,
      left: 0,
      behavior: "smooth",
    });
  } catch (error) {
    el.scrollTo(offset, 0);
  }
};

export const resetScroll = (el: Element, callback = () => {}) => {
  const onScroll = () => {
    if (el.scrollTop === 0) {
      el.removeEventListener("scroll", onScroll);
      callback();
    }
  };
  el.addEventListener("scroll", onScroll);
  onScroll();
  try {
    el.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  } catch (error) {
    el.scrollTo(0, 0);
  }
};

export const generateId = () => {
  const randomId = [
    Math.floor(Math.random() * 4000),
    Math.floor(Math.random() * 4000),
    Math.floor(Math.random() * 4000),
  ];
  return randomId.join("-");
};

export const isMobile = () => {
  return window.innerWidth < MAX_WIDTH_MOBILE_SCREEN;
};

export const isMobileDevice =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );

export const isSafari = /^((?!chrome|android).)*safari/i.test(
  navigator.userAgent,
);

export const formatNumberWithCommas = (
  input: number | string | null | undefined,
  unit?: string,
) => {
  if (input === null || input === undefined) {
    return "-";
  }

  const num = Number(input);

  const fixed = num.toFixed(1);

  const [integerPart, decimalPart] = fixed.split(".");

  const formattedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ",",
  );

  const formattedNumber = `${formattedIntegerPart}.${decimalPart}`;

  return unit ? `${formattedNumber} ${unit}` : formattedNumber;
};

export const formatTime = (time: Date, typeFormat: string) => {
  return format(
    sub(time, {
      hours: new Date().getTimezoneOffset() / -60,
    }),
    typeFormat,
  );
};

export const iOSversion = () => {
  if (/iP(hone|od|ad)/.test(navigator.userAgent)) {
    const v = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
    if (v) {
      return parseInt(v[1], 10);
    }
  }
  return -1;
};

export const resetIsContinuousLoading = () => {
  setItem(StorageKey.IS_CONTINUOUS_LOADING, "");
  store.dispatch(setIsLoading(false));
};

export const getMaxDate = () => {
  const currentDate = new Date();
  return new Date(
    currentDate.setFullYear(currentDate.getFullYear() + DEFAULT_MAX_YEAR),
  );
};

export const formatAnnouncementsDate = (
  input: string,
  isoLocaleCode: string,
  userTimeFormat: string,
) => {
  let parsedDateLocal;

  if (isoLocaleCode === "en") {
    parsedDateLocal = parse(input, "MMM d, yyyy, h:mm a", new Date());
  } else {
    parsedDateLocal = parse(input, "yyyy/MM/dd, h:mm a", new Date());
  }

  const parsedDateUTC = new Date(
    parsedDateLocal.getTime() - parsedDateLocal.getTimezoneOffset() * 60000,
  );

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zonedDate = toZonedTime(parsedDateUTC, userTimeZone);
  const outputFormat = `${userTimeFormat}, h:mm a`;

  const formattedLocal = format(zonedDate, outputFormat);
  return formattedLocal;
};

export const formatAnnouncementNoticeDate = (
  input: string,
  isoLocaleCode: string,
  userTimeFormat: string,
) => {
  let parsedDate;

  if (isoLocaleCode === "en") {
    parsedDate = parse(input, "MMM d, yyyy", new Date());
  } else {
    parsedDate = parse(input, "yyyy/MM/dd", new Date());
  }

  if (isNaN(parsedDate.getTime())) {
    throw new Error("Invalid date");
  }

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zonedDate = toZonedTime(parsedDate, userTimeZone);

  const outputFormat = userTimeFormat;
  return format(zonedDate, outputFormat);
};

export const clearMachineReportsPreferences = () => {
  const keys = [
    "machineReportsLayoutViewMode",
    "machineReportsDateViewMode",
    "machineReportsCurrentDate",
    "machineReportsFilterCondition",
    "machineReportsShowAll",
    "machineReportsPage",
    "machineReportsSearchTextMachine",
  ];

  keys.forEach((key) => {
    localStorage.removeItem(key);
  });
};

export const formatAnnouncementsBody = (
  body: string,
  localeDateStart: string,
  localeDateEnd: string,
  localeDateNotice: string,
  isoLocaleCode: string,
  userTimeFormat: string,
): { formattedBody: string; url: string | null } => {
  if (!body) return { formattedBody: "", url: null };

  const sanitize = (str: string) =>
    str.replace(/[\u200E\u202A\u202C\u202D\u202E]/g, "").trim();

  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  let updatedBody = body;

  const startRegex = new RegExp(
    `(?:${escapeRegex(
      localeDateStart,
    )}\\s*)[\\u200E]?(.+?)[\\s,、，]+(\\d{1,2}:\\d{2}\\s*[AP]M)`,
  );
  const endRegex = new RegExp(
    `(?:${escapeRegex(
      localeDateEnd,
    )}\\s*)[\\u200E]?(.+?)[\\s,、，]+(\\d{1,2}:\\d{2}\\s*[AP]M)`,
  );
  const noticeRegex = new RegExp(
    `(?:${escapeRegex(localeDateNotice)}\\s*)[\\u200E]?(.+?)$`,
    "m",
  );

  const startMatch = body.match(startRegex);
  if (startMatch?.[1] && startMatch?.[2]) {
    const rawStart = sanitize(`${startMatch[1]}, ${startMatch[2]}`);
    try {
      const formatted = formatAnnouncementsDate(
        rawStart,
        isoLocaleCode,
        userTimeFormat,
      );
      updatedBody = updatedBody.replace(
        startMatch[0],
        `${localeDateStart} ${formatted}`,
      );
    } catch (e) {
      console.warn("Invalid start date:", rawStart);
    }
  }

  const endMatch = body.match(endRegex);
  if (endMatch?.[1] && endMatch?.[2]) {
    const rawEnd = sanitize(`${endMatch[1]}, ${endMatch[2]}`);
    try {
      const formatted = formatAnnouncementsDate(
        rawEnd,
        isoLocaleCode,
        userTimeFormat,
      );
      updatedBody = updatedBody.replace(
        endMatch[0],
        `${localeDateEnd} ${formatted}`,
      );
    } catch (e) {
      console.warn("Invalid end date:", rawEnd);
    }
  }

  const noticeMatch = body.match(noticeRegex);
  if (noticeMatch?.[1]) {
    const rawNotice = sanitize(noticeMatch[1]);

    try {
      const formattedNotice = formatAnnouncementNoticeDate(
        rawNotice,
        isoLocaleCode,
        userTimeFormat,
      );

      updatedBody = updatedBody.replace(
        noticeMatch[0],
        `${localeDateNotice} ${formattedNotice}`,
      );
    } catch (e) {
      console.warn("Invalid notice date:", rawNotice);
    }
  }

  const urlMatch = body.match(/https?:\/\/\S+/);
  const url = urlMatch?.[0] ?? null;

  const urlFieldRegex =
    /URL\s*:\s*[\u200E\u202A\u202C\u202D\u202E\s]*N\/A[\u200E\u202A\u202C\u202D\u202E\s]*/gi;

  if (!url) {
    updatedBody = updatedBody.replace(urlFieldRegex, "").trim();
  }

  return { formattedBody: updatedBody, url };
};

export const isValidVersion = (version: string): boolean => {
  const versionRegex = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
  return versionRegex.test(version);
};

export const safeSliceGraphemes = (str: string, max: number): string => {
  const segmenter = new Intl.Segmenter(undefined, {
    granularity: "grapheme",
  });

  const graphemes = [...segmenter.segment(str)];
  return graphemes
    .slice(0, max)
    .map((g) => g.segment)
    .join("");
};
